import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr/dist/esm/HubConnectionBuilder';
import { HttpTransportType, LogLevel } from '@microsoft/signalr';
import httpService from '../../services/httpService';
import { ApiEndpoints } from '../../constants/appConstants';

interface InitiateDataManagementUploadResponse {
  fileKey: string;
  uploadId: string;
  isGuardDutyEnabled?: boolean;
}

// Uploads an excel file in chunks and only resolves with the fileKey once the
// AWS GuardDuty scan (pushed over the shared fileProcessingHub) confirms the file is clean
export function useDataManagementFileUpload() {
  const [connection, setConnection] = useState<any>(null);
  const scanResults = useRef<Map<string, boolean>>(new Map());
  const scanWaiters = useRef<Map<string, (isFine: boolean) => void>>(new Map());

  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.fileProcessingHub, {
        transport: HttpTransportType.ServerSentEvents,
      })
      .configureLogging(LogLevel.Information)
      .build();

    newConnection
      .start()
      .then(() => setConnection(newConnection))
      .catch((err) => console.error('Error establishing SignalR connection:', err));

    return () => {
      if (newConnection) {
        newConnection.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!connection) {
      return;
    }
    connection.on('ScanInfo', (message: any) => {
      if (!message?.fileName) {
        return;
      }
      const isFine = message.isFine === true;
      const waiter = scanWaiters.current.get(message.fileName);
      if (waiter) {
        scanWaiters.current.delete(message.fileName);
        waiter(isFine);
      } else {
        scanResults.current.set(message.fileName, isFine);
      }
    });
  }, [connection]);

  const waitForScanResult = (fileName: string): Promise<boolean> => {
    const existingResult = scanResults.current.get(fileName);
    if (existingResult !== undefined) {
      scanResults.current.delete(fileName);
      return Promise.resolve(existingResult);
    }
    return new Promise((resolve) => {
      scanWaiters.current.set(fileName, resolve);
    });
  };

  const uploadFile = async (file: File, moduleName: string): Promise<string> => {
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const tags: string[] = [];

    const initiateResult = await httpService.post<InitiateDataManagementUploadResponse>(
      ApiEndpoints.initiateResourceMultipartUploadUrl,
      {
        uploadType:moduleName,
        fileName: file.name,
        connectionId: connection?.connectionId,
      }
    );

    const { uploadId, fileKey, isGuardDutyEnabled } = initiateResult.data;

    for (let i = 0; i < totalChunks; i += 1) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk, file.name);
      formData.append('chunkIndex', i.toString());
      formData.append('uploadId', uploadId);
      formData.append('fileKey', fileKey);
      formData.append('isLastChunk', (i === totalChunks - 1).toString());
      formData.append('uploadType', moduleName);

      const chunkResult = await httpService.post<{ tag?: string }>(
        ApiEndpoints.uploadResourceMultipart,
        formData
      );

      if (chunkResult.data?.tag) {
        tags.push(chunkResult.data.tag);
      }
    }

    await httpService.post(ApiEndpoints.completeResourceMultipartUploadUrl, {
      uploadId,
      fileKey,
      tags,
      uploadType:moduleName
    });

    if (isGuardDutyEnabled) {
      const isFine = await waitForScanResult(file.name);
      if (!isFine) {
        throw new Error('File failed security scan.');
      }
    }

    return fileKey;
  };

  return { uploadFile };
}
